import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { deleteModel, listCities, listModels } from '../../../services/UserService';
import './Models.css';

const getModelId = (model) => model.uuid || model.id;
const getModelDisplayId = (model) => model.uuid ? model.uuid.slice(0, 8) : model.id;
const getStatusClass = (status) => String(status || 'unknown').toLowerCase().replace(/\s+/g, '-');
const getCampaignStatusLabel = (campaign) => {
    const status = String(campaign?.status || '').toLowerCase();
    if (status === 'auto') return 'Auto-pay';
    if (status === 'active') return 'Active';
    if (status === 'inactive') return 'Inactive';
    return '—';
};
const getCampaignStatusClass = (campaign) => String(campaign?.status || 'none').toLowerCase().replace(/\s+/g, '-');
const getCampaignTypeLabel = (campaign) => {
    const campaignType = String(campaign?.type || campaign?.data?.type || '').toLowerCase();
    if (campaignType === 'trial') return 'Trial';
    return ({ 1: '3 Days', 2: '7 Days', 3: '30 Days' })[Number(campaign?.budget)] || '—';
};
const getPhotoUrl = (photo) => typeof photo === 'string'
    ? photo
    : photo?.url || photo?.photoUrl || photo?.imageUrl || photo?.src || '';
const getModelPhotos = (model) => [...(Array.isArray(model.photos) ? model.photos : []), model.image]
    .map(getPhotoUrl)
    .filter((photo, index, photos) => photo && photos.indexOf(photo) === index);

const ModelPhotoSlider = ({ model }) => {
    const photos = getModelPhotos(model);
    const [slideIndex, setSlideIndex] = useState(0);
    const modelId = getModelId(model);
    const modelName = model.name || 'Unnamed model';

    if (photos.length === 0) {
        return <div className="model-list-photo model-list-photo-empty" aria-label={`${modelName} has no photos`}>No photo</div>;
    }

    const moveSlide = (event, direction) => {
        event.preventDefault();
        event.stopPropagation();
        setSlideIndex(current => (current + direction + photos.length) % photos.length);
    };

    return (
        <div className="model-list-photo" role="group" aria-label={`${modelName} photos`}>
            <Link
                className="model-list-photo-link"
                to={`/model-edit/${modelId}`}
                aria-label={`Open ${modelName} photo ${slideIndex + 1}`}
            >
                <img src={photos[slideIndex]} alt={`${modelName} ${slideIndex + 1}`} loading="lazy" />
            </Link>
            {photos.length > 1 && (
                <>
                    <button type="button" className="model-photo-control previous" onClick={(event) => moveSlide(event, -1)} aria-label={`Previous photo for ${modelName}`}>&lsaquo;</button>
                    <button type="button" className="model-photo-control next" onClick={(event) => moveSlide(event, 1)} aria-label={`Next photo for ${modelName}`}>&rsaquo;</button>
                    <span className="model-photo-count" aria-live="polite">{slideIndex + 1}/{photos.length}</span>
                </>
            )}
        </div>
    );
};

const Models = () => {
    const navigate = useNavigate();
    const [models, setModels] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoadingModels, setIsLoadingModels] = useState(true);
    const [modelsError, setModelsError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, model: null });
    const [isDeletingModel, setIsDeletingModel] = useState(false);
    const [deleteModelError, setDeleteModelError] = useState('');

    useEffect(() => {
        let isMounted = true;

        const loadModels = async () => {
            setModelsError('');
            try {
                const result = await listModels();
                if (isMounted) setModels(result.items);
            } catch (loadError) {
                if (isMounted) {
                    setModelsError(loadError instanceof TypeError ? 'Unable to connect to the models service.' : loadError.message);
                }
            } finally {
                if (isMounted) setIsLoadingModels(false);
            }
        };

        loadModels();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        listCities().then(items => {
            if (isMounted) setCities(items);
        }).catch(() => {
            if (isMounted) setCities([]);
        });
        return () => {
            isMounted = false;
        };
    }, []);

    const cityNames = Object.fromEntries(cities.map(city => [city.uuid, city.name || city.title || city.slug]));

    const handleEditModel = (model) => {
        navigate(`/model-edit/${getModelId(model)}`);
    };

    const handleDeleteModel = (model) => {
        setDeleteModelError('');
        setDeleteConfirm({ show: true, model });
    };

    const confirmDeleteModel = async () => {
        if (!deleteConfirm.model) return;
        const modelToDelete = deleteConfirm.model;
        setDeleteModelError('');
        setIsDeletingModel(true);
        try {
            await deleteModel(getModelId(modelToDelete));
            setModels(currentModels => currentModels.filter(model => getModelId(model) !== getModelId(modelToDelete)));
            setDeleteConfirm({ show: false, model: null });
        } catch (deleteError) {
            setDeleteModelError(deleteError instanceof TypeError ? 'Unable to connect to the models service.' : deleteError.message);
        } finally {
            setIsDeletingModel(false);
        }
    };

    const cancelDeleteModel = () => {
        if (isDeletingModel) return;
        setDeleteModelError('');
        setDeleteConfirm({ show: false, model: null });
    };

    return (
        <div className="models">
            <NavigationMenu />
            <div className="models-content">
                <header className="models-header">
                    <h1 className="models-title">Models</h1>
                    <p className="models-subtitle">Manage agency models and profiles</p>
                </header>
                <main className="models-main">
                    <div className="table-container">
                        <div className="table-header">
                            <h2 className="table-title">All Models</h2>
                        </div>
                        <div className="table-scroll">
                            <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Photo</th>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>City</th>
                                    <th>Status</th>
                                    <th>Campaign Status</th>
                                    <th>Campaign Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingModels && (
                                    <tr>
                                        <td colSpan="8" className="models-feedback">Loading models...</td>
                                    </tr>
                                )}
                                {!isLoadingModels && modelsError && (
                                    <tr>
                                        <td colSpan="8" className="models-feedback models-error" role="alert">{modelsError}</td>
                                    </tr>
                                )}
                                {!isLoadingModels && !modelsError && models.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="models-feedback">No models found.</td>
                                    </tr>
                                )}
                                {!isLoadingModels && !modelsError && models.map(model => {
                                    const modelId = getModelId(model);
                                    const status = model.status || 'Unknown';
                                    return (
                                        <tr key={modelId}>
                                            <td className="model-photo-cell"><ModelPhotoSlider model={model} /></td>
                                            <td title={model.uuid || undefined}>#{getModelDisplayId(model)}</td>
                                            <td>
                                                <Link className="model-name-link" to={`/model-edit/${modelId}`}>{model.name || 'Unnamed model'}</Link>
                                            </td>
                                            <td>{cityNames[model.city] || model.city || '—'}</td>
                                            <td>
                                                <span className={`status-badge ${getStatusClass(status)}`}>
                                                    {status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge campaign-status-badge ${getCampaignStatusClass(model.campaign)}`}>
                                                    {getCampaignStatusLabel(model.campaign)}
                                                </span>
                                            </td>
                                            <td className="campaign-type-cell">{getCampaignTypeLabel(model.campaign)}</td>
                                            <td>
                                                <button className="action-btn edit" onClick={() => handleEditModel(model)}>Edit</button>
                                                <button className="action-btn delete" onClick={() => handleDeleteModel(model)}>Delete</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>

            {deleteConfirm.show && (
                <div className="models-modal-overlay" role="presentation">
                    <div className="models-modal" role="dialog" aria-modal="true" aria-labelledby="delete-model-title">
                        <div className="models-modal-header">
                            <h3 id="delete-model-title">Move Model to Trash</h3>
                            <button className="models-modal-close" onClick={cancelDeleteModel} disabled={isDeletingModel} aria-label="Close">
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="models-modal-body">
                            <p>Move <strong>{deleteConfirm.model.name || 'this model'}</strong> to trash?</p>
                            <p className="models-trash-notice"><i className="fa-solid fa-trash-can"></i> The local model profile can be restored from Trash Bin. Connected resources are not deleted.</p>
                            {deleteModelError && <span className="models-modal-error" role="alert">{deleteModelError}</span>}
                        </div>
                        <div className="models-modal-footer">
                            <button className="models-modal-button cancel" onClick={cancelDeleteModel} disabled={isDeletingModel}>Cancel</button>
                            <button className="models-modal-button delete" onClick={confirmDeleteModel} disabled={isDeletingModel}>
                                {isDeletingModel ? 'Moving...' : 'Move to Trash'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Models;
